import React from 'react';
import PropTypes from 'prop-types';
import { strings } from '../../../../localization';
import SimpleSideBySideNav from '../../../../components/common/SimpleSideBySideNav';
import { 
    MILES_TO_KM_FACTOR,
    MAX_SCORE_TREND_PREVIOUS_PERIODS,
    timePeriodShortLabelForDate } from '../SafetyScoreModuleGroup';
import ProgressOverlay from '../../../../components/common/ProgressOverlay';
import ResizeThrottler from '../../../../components/ResizeThrottler';
import {
    NOT_APPLICABLE_SCORE,
    NOT_APPLICABLE_SCORE_PLACEHOLDER,
    SUPPORTED_TIME_PERIOD_TYPES,
    REPORT_BY_TYPES,
    colorForSafetyScore,
    localizedSortEventTypeIds } from '../SafetyScore';

const DYNAMIC_STICKY_FOOTER_HEIGHT = "2.85714286rem"; // 40px @ font size 14px
const RESIZE_EVENT_HANDLER_THROTTLE_INTERVAL_MS = 100;
const APPROXIMATE_TABLE_HEADER_PLUS_ONE_ROW_HEIGHT = 60;

const SORT_BY_TYPE = {
    ENTITY: "ENTITY",
    DISTANCE: "DISTANCE",
    DURATION: "DURATION",
    EVENT_TYPE: "EVENT_TYPE",
    SCORE: "SCORE"
};

export default class ScoreDetailsTable extends React.PureComponent {

    static propTypes = {
        totalTimePeriodsLoaded: PropTypes.number.isRequired,
        selectedTimePeriod: PropTypes.number.isRequired,
        onChangeSelectedTimePeriod: PropTypes.func.isRequired,
        unitOfMeasure: PropTypes.string.isRequired,
        selectedTimePeriodType: PropTypes.string.isRequired,
        selectedReportBy: PropTypes.string.isRequired,
        availableEntities: PropTypes.array.isRequired,
        availableEventsFactors: PropTypes.array.isRequired,
        selectedScoreThresholds: PropTypes.object.isRequired,
        periodStartDate: PropTypes.instanceOf(Date),
        periodEndDate: PropTypes.instanceOf(Date),
        totalKMs: PropTypes.number.isRequired,
        totalSeconds: PropTypes.number.isRequired,
        score: PropTypes.number.isRequired,
        scoreSummariesByEntityId: PropTypes.object,
        tripSummariesByEntityId: PropTypes.object
    };

    constructor(props) {
        super(props);

        this.tableContentBodyRef = React.createRef();

        const eventsFactorsWithNoZeroWeight = [];
        const eventsFactorWeightsById = {};
        this.props.availableEventsFactors.forEach(eventFactor => {
            if (eventFactor.weight > 0) {
                eventsFactorsWithNoZeroWeight.push(eventFactor);
                eventsFactorWeightsById[eventFactor.type] = eventFactor.weight;
            }
        });

        this.state = {
            footerSticky: false,
            sortBy: SORT_BY_TYPE.ENTITY,
            sortBySubType: null, // when softBy is EVENT_TYPE, this is the event type
            eventTypeColumnIds: localizedSortEventTypeIds(eventsFactorsWithNoZeroWeight),
            eventsFactorWeightsById: eventsFactorWeightsById,
            sortDirection: "asc",
            tableContentBodyWidth: "auto"
        };
    };

    componentDidMount() {
        this.updateStickyFooterPosition();
    };

    componentDidUpdate(prevProps) {

        this.updateStickyFooterPosition();

        if (prevProps.availableEventsFactors !== this.props.availableEventsFactors) {

            const eventsFactorsWithNoZeroWeight = [];
            const eventsFactorWeightsById = {};
            this.props.availableEventsFactors.forEach(eventFactor => {
                if (eventFactor.weight > 0) {
                    eventsFactorsWithNoZeroWeight.push(eventFactor);
                    eventsFactorWeightsById[eventFactor.type] = eventFactor.weight;
                }
            });    

            this.setState({
                sortBy: SORT_BY_TYPE.ENTITY,
                sortBySubType: null,
                eventTypeColumnIds: localizedSortEventTypeIds(eventsFactorsWithNoZeroWeight),
                eventsFactorWeightsById: eventsFactorWeightsById,
                sortDirection: "asc"
            });
        }
    };

    updateStickyFooterPosition = () => {

        if (!this.tableContentBodyRef) {
            return;
        }
            
        if (this.tableContentBodyRef.current) {

            const tableContentBody = this.tableContentBodyRef.current;
            const tableContentBodyRect = tableContentBody.getBoundingClientRect();

            if (tableContentBodyRect.bottom > window.innerHeight &&
                tableContentBodyRect.top < window.innerHeight - APPROXIMATE_TABLE_HEADER_PLUS_ONE_ROW_HEIGHT) {
                if (this.state.footerSticky) {
                    if (tableContentBodyRect.width + "px" !== this.state.tableContentBodyWidth) {
                        this.setState({
                            tableContentBodyWidth: tableContentBodyRect.width + "px"
                        });
                    }
                    return;
                }
                this.setState({
                    footerSticky: true,
                    tableContentBodyWidth: tableContentBodyRect.width + "px"
                });
            } else {
                this.setState({
                    footerSticky: false
                });
            }
        }
    };

    durationShortDescription = (totalSeconds) => {
        let duration = "";
        if (totalSeconds >= 3600) {
            duration = Math.floor(totalSeconds / 3600) + " " + strings.duration_label_short_hours;
        } else if (totalSeconds >= 60) {
            duration = Math.floor(totalSeconds / 60) + " " + strings.duration_label_short_minutes;
        } else {
            duration = totalSeconds + " " + strings.duration_label_short_seconds;
        }
        return duration;
    };

    sortedDistplayItems = () => {
        // return format is { tableBodyItems: [<Entity>]}, tableFooterSummaryItems: <Entity> }
        // Entity is an object with properties: id, ENTITY, DISTANCE, DURATION, SCORE, EVENT_TYPE
        //    where EVENT_TYPE is an array of scores, sorted by event type localized name.
        const {
            sortBy,
            sortBySubType,
            sortDirection
        } = this.state;

        const {
            score,
            totalKMs,
            totalSeconds,
            scoreSummariesByEntityId,
            tripSummariesByEntityId 
        } = this.props;

        const sortedItems = [];
        const summaryEntity = {}; // with the exception of entity id, this has same properts as sortedItems's entities, but values are totals and averages

        if (Object.keys(scoreSummariesByEntityId).length === 0 ||
            Object.keys(tripSummariesByEntityId).length === 0) {
            // no data to display
            return {
                tableBodyItems: null,
                tableFooterSummaryItems: null
            };
        }

        summaryEntity[SORT_BY_TYPE.ENTITY] = strings.score_summary_totals_and_averages_label;
        summaryEntity[SORT_BY_TYPE.DISTANCE] = Math.round(totalKMs * 10) / 10; // round distance to the nearest first decimal place
        summaryEntity[SORT_BY_TYPE.DURATION] = this.durationShortDescription(totalSeconds);
        summaryEntity[SORT_BY_TYPE.SCORE] = Math.round(score * 10) / 10; // round the score to the nearest first decimal place
        summaryEntity[SORT_BY_TYPE.EVENT_TYPE] = []; // array of totals averages, sorted by event type localized name

        const selectedReportBy = this.props.selectedReportBy;
        let totalApplicableEntities = 0;
        const averagesForEachEventType = {};

        Object.keys(scoreSummariesByEntityId).forEach(entityId => {

            const scoreSummary = scoreSummariesByEntityId[entityId];
            const tripSummary = tripSummariesByEntityId[entityId];

            if (!scoreSummary || !tripSummary) {
                console.warn("ScoreDetailsTable: missing scoreSummary or tripSummary for entityId: " + entityId);
                return;
            }

            const entity = {
                id: entityId
            };

            const driverName = scoreSummary.driverName ? scoreSummary.driverName : strings.unknown;
            const vehicleName = scoreSummary.vehicleName ? scoreSummary.vehicleName : strings.unknown;
            entity[SORT_BY_TYPE.ENTITY] = selectedReportBy === REPORT_BY_TYPES.DRIVER ? driverName : vehicleName;

            let distance = tripSummary.totalKMs;
            // round distance to the nearest first decimal place
            distance = Math.round(distance * 10) / 10; // in kms
            entity[SORT_BY_TYPE.DISTANCE] = distance;

            entity[SORT_BY_TYPE.DURATION] = tripSummary.totalSeconds;

            let score = scoreSummary.score;
            if (score === NOT_APPLICABLE_SCORE) {
                entity[SORT_BY_TYPE.SCORE] = NOT_APPLICABLE_SCORE_PLACEHOLDER;
                entity[SORT_BY_TYPE.EVENT_TYPE] = this.state.eventTypeColumnIds.map(() => {
                    return NOT_APPLICABLE_SCORE_PLACEHOLDER;
                });
                sortedItems.push(entity);
                return;
            }
            totalApplicableEntities += 1;

            // round the score to the nearest first decimal place
            score = Math.round(score * 10) / 10;
            entity[SORT_BY_TYPE.SCORE] = score;

            entity[SORT_BY_TYPE.EVENT_TYPE] = []; // array of totals, sorted by event type localized name
            this.state.eventTypeColumnIds.forEach(eventType => {

                if (!scoreSummary.eventTypes.hasOwnProperty(eventType)) {
                    console.warn("ScoreDetailsTable: scoreSummary missing selected event eventType: " + eventType);
                    entity[SORT_BY_TYPE.EVENT_TYPE].push(0);
                    return;
                }
                let scoreForEventType = scoreSummary.eventTypes[eventType].score;
                // round the score to the nearest first decimal place
                scoreForEventType = Math.round(scoreForEventType * 10) / 10;
                entity[SORT_BY_TYPE.EVENT_TYPE].push(scoreForEventType);
                // prepare for average calculation
                averagesForEachEventType[eventType] = averagesForEachEventType[eventType] ? averagesForEachEventType[eventType] + scoreForEventType : scoreForEventType;
            });

            sortedItems.push(entity);
        });

        // calculate averages for each event type
        this.state.eventTypeColumnIds.forEach(eventType => {
            if (!totalApplicableEntities) {
                averagesForEachEventType[eventType] = NOT_APPLICABLE_SCORE_PLACEHOLDER;
                return;
            }
            const sumOfAllScores = averagesForEachEventType[eventType];
            const averageScore = sumOfAllScores / totalApplicableEntities;
            // round the score to the nearest first decimal place
            averagesForEachEventType[eventType] = Math.round(averageScore * 10) / 10;
        });

        summaryEntity[SORT_BY_TYPE.EVENT_TYPE] = this.state.eventTypeColumnIds.map(eventType => {
            return averagesForEachEventType[eventType];
        });

        sortedItems.sort((a, b) => {
            let result = 0;
            switch (sortBy) {
                case SORT_BY_TYPE.ENTITY:
                    result = a[SORT_BY_TYPE.ENTITY].localeCompare(b[SORT_BY_TYPE.ENTITY]);
                    break;
                case SORT_BY_TYPE.DISTANCE:
                    result = a[SORT_BY_TYPE.DISTANCE] - b[SORT_BY_TYPE.DISTANCE];
                    break;
                case SORT_BY_TYPE.DURATION:
                    result = a[SORT_BY_TYPE.DURATION] - b[SORT_BY_TYPE.DURATION];
                    break;
                case SORT_BY_TYPE.SCORE:
                    // Handle NOT_APPLICABLE_SCORE_PLACEHOLDER values (strings like "N/A")
                    const aScore = a[SORT_BY_TYPE.SCORE];
                    const bScore = b[SORT_BY_TYPE.SCORE];
                    
                    if (aScore === NOT_APPLICABLE_SCORE_PLACEHOLDER && bScore === NOT_APPLICABLE_SCORE_PLACEHOLDER) {
                        result = 0; // Both are N/A, consider them equal
                    } else if (aScore === NOT_APPLICABLE_SCORE_PLACEHOLDER) {
                        return 1; // N/A values ALWAYS go to the end, regardless of sort direction
                    } else if (bScore === NOT_APPLICABLE_SCORE_PLACEHOLDER) {
                        return -1; // N/A values ALWAYS go to the end, regardless of sort direction
                    } else {
                        result = aScore - bScore; // Normal numeric comparison
                    }
                    break;
                case SORT_BY_TYPE.EVENT_TYPE:
                    // Handle NOT_APPLICABLE_SCORE_PLACEHOLDER values for event types
                    const eventIndex = this.state.eventTypeColumnIds.indexOf(sortBySubType);
                    const aEventScore = a[SORT_BY_TYPE.EVENT_TYPE][eventIndex];
                    const bEventScore = b[SORT_BY_TYPE.EVENT_TYPE][eventIndex];
                    
                    if (aEventScore === NOT_APPLICABLE_SCORE_PLACEHOLDER && bEventScore === NOT_APPLICABLE_SCORE_PLACEHOLDER) {
                        result = 0;
                    } else if (aEventScore === NOT_APPLICABLE_SCORE_PLACEHOLDER) {
                        return 1; // N/A values ALWAYS go to the end, regardless of sort direction
                    } else if (bEventScore === NOT_APPLICABLE_SCORE_PLACEHOLDER) {
                        return -1; // N/A values ALWAYS go to the end, regardless of sort direction
                    } else {
                        result = aEventScore - bEventScore;
                    }
                    break;
                default:
                    break;
            }

            return sortDirection === "asc" ? result : -result;
        });

        return {
            tableBodyItems: sortedItems,
            tableFooterSummaryItems: summaryEntity
        };
    };

    handleSortByClick = (event) => {
        const sortBy = event.currentTarget.id;
        
        if (this.state.sortBy === sortBy) {
            this.setState({
                sortDirection: this.state.sortDirection === "asc" ? "desc" : "asc"
            });
        } else {
            this.setState({
                sortBy,
                sortBySubType: null,
                sortDirection: "asc"
            });
        }
    };

    handleEventSortByClick = (event) => {
        const sortBySubType = event.currentTarget.id;
        if (this.state.sortBy === SORT_BY_TYPE.EVENT_TYPE && this.state.sortBySubType === sortBySubType) {
            this.setState({
                sortDirection: this.state.sortDirection === "asc" ? "desc" : "asc"
            });
        }
        else {
            this.setState({
                sortBy: SORT_BY_TYPE.EVENT_TYPE,
                sortBySubType,
                sortDirection: "asc"
            });
        }
    };

    onTimePeriodClickNext = (event) => {
        let selectedTimePeriod = this.props.selectedTimePeriod - 1; // time periods are in reverse order (0 is current, 1+ is historical)
        if (selectedTimePeriod < 0) {
            selectedTimePeriod = MAX_SCORE_TREND_PREVIOUS_PERIODS;
        }
        this.props.onChangeSelectedTimePeriod(selectedTimePeriod);
    }

    onTimePeriodClickPrevious = (event) => {
        let selectedTimePeriod = this.props.selectedTimePeriod + 1; // time periods are in reverse order (0 is current, 1+ is historical)
        if (selectedTimePeriod > MAX_SCORE_TREND_PREVIOUS_PERIODS) {
            selectedTimePeriod = 0;
        }
        this.props.onChangeSelectedTimePeriod(selectedTimePeriod);
    }

    sortItemLabelForColumn = (columnId, subtype = undefined) => {

        if (this.state.sortBy != columnId) {
            return null;
        }

        if (columnId === SORT_BY_TYPE.EVENT_TYPE) {
            if (this.state.sortBySubType === subtype) {
                return this.renderSortDirectionIcon();
            }
            return null;
        }

        return this.renderSortDirectionIcon();
    };

    renderSortDirectionIcon = () => {

        return (
            <div className="sort-direction-icon">
                {this.state.sortDirection === "asc" ?
                    <i className="material-icons">arrow_drop_up</i>
                    :
                    <i className="material-icons">arrow_drop_down</i>
                }
            </div>
        );
    }
        
    render() {

        const { tableBodyItems,
            tableFooterSummaryItems } = this.sortedDistplayItems();

        const periodStartDateString = timePeriodShortLabelForDate(this.props.periodStartDate);
        let periodEndDateString;

        let scoreDetailsDescription = strings.score_details_table_description_showing_x_entity_type;

        if (this.props.selectedReportBy === REPORT_BY_TYPES.DRIVER  ) {
            scoreDetailsDescription = strings.formatString(scoreDetailsDescription, strings.score_entity_type_driver_lowercase);
        } else {
            scoreDetailsDescription = strings.formatString(scoreDetailsDescription, strings.score_entity_type_vehicle_lowercase);
        }

        if (this.props.selectedTimePeriod === 0) {
            periodEndDateString = strings.score_time_period_present;
            switch (this.props.selectedTimePeriodType) {
                case SUPPORTED_TIME_PERIOD_TYPES.WEEKLY:
                    periodEndDateString += " (" + strings.score_time_period_current_week + ")";
                    break;
                case SUPPORTED_TIME_PERIOD_TYPES.MONTHLY:
                    periodEndDateString += " (" + strings.score_time_period_current_month + ")";
                    break;
                case SUPPORTED_TIME_PERIOD_TYPES.QUARTERLY:
                    periodEndDateString += " (" + strings.score_time_period_current_quarter + ")";
                    break;
                default:
                    break;
            }
        } else {
            periodEndDateString = timePeriodShortLabelForDate(this.props.periodEndDate);
        }

        return (
            <section className="score-details-table">
                <header className="score-header">
                    <h6>
                        <span>
                            {strings.score_details_table_header}
                        </span>
                        <br/>
                        {periodStartDateString && periodEndDateString &&
                        <div className="time-period">
                            <SimpleSideBySideNav
                                middleElement={<span>{periodStartDateString + " - " + periodEndDateString}</span>}
                                heightStyle="16px"
                                iconWidthStyle="8px"
                                leftDisabled={this.props.selectedTimePeriod === MAX_SCORE_TREND_PREVIOUS_PERIODS}
                                rightDisabled={this.props.selectedTimePeriod === 0}
                                onLeftClick={this.onTimePeriodClickPrevious}
                                onRightClick={this.onTimePeriodClickNext}
                            />
                        </div>
                        }
                    </h6>
                    <div>
                        {scoreDetailsDescription}
                    </div>
                </header> 
                <section className="score-details-table-content">
                    <div className="score-details-table-header">
                        <div className="score-details-table-row">
                            <div className={this.state.sortBy === SORT_BY_TYPE.ENTITY ? "sdtable-cell-entity sdtable-cell-bold" : "sdtable-cell-entity"}>
                                <button className="sdtable-cell-truncated-button" id={SORT_BY_TYPE.ENTITY} onClick={this.handleSortByClick}>
                                    <div className="sdtable-cell-truncated-label">
                                        {this.props.selectedReportBy === REPORT_BY_TYPES.DRIVER ?
                                            strings.score_driver
                                            :
                                            strings.score_vehicle
                                        }
                                    </div>
                                    {this.sortItemLabelForColumn(SORT_BY_TYPE.ENTITY)}
                                </button>
                            </div>
                            <div className={this.state.sortBy === SORT_BY_TYPE.DISTANCE ? "sdtable-cell-distance sdtable-cell-bold" : "sdtable-cell-distance"}>
                                <button className="sdtable-cell-truncated-button" id={SORT_BY_TYPE.DISTANCE} onClick={this.handleSortByClick}>
                                    <div className="sdtable-cell-truncated-label">
                                        {strings.db_raven_trip_distance_label}
                                        {this.props.unitOfMeasure === "METRIC" ?
                                            " (" + strings.distance_label_short_metric + ")"
                                            :
                                            " (" + strings.distance_label_short_imperial + ")"
                                        }
                                    </div>
                                    {this.sortItemLabelForColumn(SORT_BY_TYPE.DISTANCE)}
                                </button>
                            </div>
                            <div className={this.state.sortBy === SORT_BY_TYPE.DURATION ? "sdtable-cell-duration sdtable-cell-bold" : "sdtable-cell-duration"} >
                                <button className="sdtable-cell-truncated-button" id={SORT_BY_TYPE.DURATION} onClick={this.handleSortByClick}>
                                    <div className="sdtable-cell-truncated-label">
                                        {strings.db_raven_trip_duration_label}
                                    </div>
                                    {this.sortItemLabelForColumn(SORT_BY_TYPE.DURATION)}
                                </button>
                            </div>
                            {this.state.eventTypeColumnIds.map((eventType, index) => {
                                return (
                                    <div key={index} className={this.state.sortBy === SORT_BY_TYPE.EVENT_TYPE && eventType === this.state.sortBySubType ? "sdtable-cell-event-type sdtable-cell-bold" : "sdtable-cell-event-type"} >
                                        <button
                                            className="sdtable-cell-truncated-button"
                                            id={eventType}
                                            onClick={this.handleEventSortByClick}
                                        >
                                            <div className="sdtable-cell-truncated-label">
                                                {document.getEventName({type: eventType})}
                                                {this.state.eventsFactorWeightsById && this.state.eventsFactorWeightsById[eventType] ?
                                                    " (" + this.state.eventsFactorWeightsById[eventType] + ")"
                                                    :
                                                    null
                                                }
                                            </div>
                                            {this.sortItemLabelForColumn(SORT_BY_TYPE.EVENT_TYPE, eventType)}
                                        </button>
                                    </div>
                                );
                            })}
                            <div className={this.state.sortBy === SORT_BY_TYPE.SCORE ? "sdtable-cell-event-score sdtable-cell-bold" : "sdtable-cell-event-score"} >
                                <button className="sdtable-cell-truncated-button" id={SORT_BY_TYPE.SCORE} onClick={this.handleSortByClick}>
                                    <div className="sdtable-cell-truncated-label">
                                        {strings.score_y_axis_label}
                                    </div>
                                    {this.sortItemLabelForColumn(SORT_BY_TYPE.SCORE)}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div
                        className="score-details-table-body"
                        ref={this.tableContentBodyRef}
                        style={{
                            marginBottom: this.state.footerSticky ? DYNAMIC_STICKY_FOOTER_HEIGHT : 0
                        }}
                    >
                        {tableBodyItems && tableBodyItems.map((item, index) => {
                            const itemScore = item[SORT_BY_TYPE.SCORE];
                            return (
                                <div className="score-details-table-row" key={index}>
                                    <div className="sdtable-cell-entity">
                                        <div className="sdtable-cell-truncated-label">
                                            {item[SORT_BY_TYPE.ENTITY]}
                                        </div>
                                    </div>
                                    <div className="sdtable-cell-distance">
                                        {this.props.unitOfMeasure === "METRIC" ?
                                            item[SORT_BY_TYPE.DISTANCE]
                                            :
                                            Math.round(item[SORT_BY_TYPE.DISTANCE] * (1/MILES_TO_KM_FACTOR) * 10) / 10
                                        }
                                    </div>
                                    <div className="sdtable-cell-duration">
                                        {this.durationShortDescription(item[SORT_BY_TYPE.DURATION])}
                                    </div>
                                    {item[SORT_BY_TYPE.EVENT_TYPE].map((eventTypeScore, index) => {
                                        const displayedScore = eventTypeScore === NOT_APPLICABLE_SCORE ? NOT_APPLICABLE_SCORE_PLACEHOLDER : eventTypeScore;
                                        return (
                                            <div className="sdtable-cell-event-type" key={index}>
                                                {displayedScore}
                                            </div>
                                        );
                                    })}
                                    <div className="sdtable-cell-event-score sdtable-cell-bold" style={{
                                        backgroundColor: colorForSafetyScore(itemScore, this.props.selectedScoreThresholds),
                                        color: "white"}}>
                                        {itemScore}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div
                        className="score-details-table-footer"
                        style={{
                            position: this.state.footerSticky ? "fixed" : "relative",
                            bottom: this.state.footerSticky ? 0 : "auto",
                            width: this.state.footerSticky ? this.state.tableContentBodyWidth : "auto",
                            height: DYNAMIC_STICKY_FOOTER_HEIGHT
                        }}
                    >
                        {tableFooterSummaryItems &&
                            <div className="score-details-table-summary-row">
                                <div className="sdtable-cell-entity">
                                    {tableFooterSummaryItems[SORT_BY_TYPE.ENTITY]}
                                </div>
                                <div className="sdtable-cell-distance">
                                    {this.props.unitOfMeasure === "METRIC" ?
                                        tableFooterSummaryItems[SORT_BY_TYPE.DISTANCE]
                                        :
                                        Math.round(tableFooterSummaryItems[SORT_BY_TYPE.DISTANCE] * (1/MILES_TO_KM_FACTOR) * 10) / 10
                                    }
                                </div>
                                <div className="sdtable-cell-duration">
                                    {tableFooterSummaryItems[SORT_BY_TYPE.DURATION]}
                                </div>
                                {tableFooterSummaryItems[SORT_BY_TYPE.EVENT_TYPE].map((eventTypeScore, index) => {
                                    return (
                                        <div className="sdtable-cell-event-type" key={index}>
                                            {eventTypeScore}
                                        </div>
                                    );
                                }
                                )}
                                <div className="sdtable-cell-event-score" style={{
                                    backgroundColor: colorForSafetyScore(tableFooterSummaryItems[SORT_BY_TYPE.SCORE], this.props.selectedScoreThresholds),
                                    color: "white"
                                }}>
                                    {tableFooterSummaryItems[SORT_BY_TYPE.SCORE]}
                                </div>
                            </div>
                        }
                    </div>
                </section>
                {this.props.totalTimePeriodsLoaded === 0 &&
                    <ProgressOverlay />
                }
                <ResizeThrottler interval={RESIZE_EVENT_HANDLER_THROTTLE_INTERVAL_MS} callback={this.updateStickyFooterPosition} />
            </section>
        );
    };
}
